import { useEffect, useMemo, useState } from "react";
import { Award, IndianRupee, Info, Pencil, Percent, Truck, type LucideIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { CompactStatCard, FormInput, FormSelect, PageHeader, StatsGrid } from "@/components/shared";
import { usePlatformSettings, useUpdatePlatformSettingsMutation } from "@/hooks/queries";
import type {
  BvSettingsDto,
  CommissionSettingsDto,
  PlatformSettingsDto,
  PayoutSettingsDto,
} from "@/types/settings.dto";
import { getApiErrorMessage } from "@/services/httpClient";
import { useToast } from "@/hooks/use-toast";

type SettingsSection = "commission" | "payout" | "bv";

type SectionMeta = {
  key: SettingsSection;
  title: string;
  description: string;
  icon: LucideIcon;
};

const SECTIONS: SectionMeta[] = [
  {
    key: "commission",
    title: "Commission",
    description: "Store & delivery fees",
    icon: Percent,
  },
  {
    key: "payout",
    title: "Payouts",
    description: "Schedules & minimums",
    icon: IndianRupee,
  },
  {
    key: "bv",
    title: "BV & Rewards",
    description: "Activation & income pools",
    icon: Award,
  },
];

function sectionSummary(settings: PlatformSettingsDto, key: SettingsSection): string {
  switch (key) {
    case "commission":
      return `Store ${settings.commission.defaultStoreCommission}% · Delivery ₹${settings.commission.deliveryCommissionPerOrder}`;
    case "payout":
      return `Store ${settings.payout.storePayoutFrequency} · Min ₹${settings.payout.minimumPayout}`;
    case "bv": {
      const total =
        settings.bv.selfCashbackPercent +
        settings.bv.levelCashbackPercent +
        settings.bv.rewardPercent;
      return `Activate ${settings.bv.activationBvThreshold} BV · Pool ${total.toFixed(0)}%`;
    }
    default:
      return "";
  }
}

function isBvValid(bv: BvSettingsDto) {
  const total = bv.selfCashbackPercent + bv.levelCashbackPercent + bv.rewardPercent;
  return Math.abs(total - 100) <= 0.01 && bv.activationBvThreshold > 0;
}

const PlatformSettingsSection = () => {
  const { toast } = useToast();
  const { data, isLoading } = usePlatformSettings();
  const updateSettings = useUpdatePlatformSettingsMutation();
  const [settings, setSettings] = useState<PlatformSettingsDto | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection | null>(null);
  const [draft, setDraft] = useState<PlatformSettingsDto | null>(null);

  useEffect(() => {
    if (data) setSettings(data);
  }, [data]);

  const activeMeta = useMemo(
    () => SECTIONS.find((s) => s.key === activeSection) ?? null,
    [activeSection],
  );

  if (isLoading || !settings) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const openEdit = (key: SettingsSection) => {
    setActiveSection(key);
    setDraft(structuredClone(settings));
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!draft || !activeSection) return;

    if (activeSection === "bv" && !isBvValid(draft.bv)) {
      toast({
        title: "BV settings need attention",
        description:
          draft.bv.activationBvThreshold <= 0
            ? "Activation BV threshold must be positive."
            : "Self + Level + Reward percent must equal 100.",
        variant: "destructive",
      });
      return;
    }

    try {
      const payload =
        activeSection === "commission"
          ? { commission: draft.commission }
          : activeSection === "payout"
            ? { payout: draft.payout }
            : { bv: draft.bv };

      const updated = await updateSettings.mutateAsync(payload);
      setSettings(updated);
      toast({ title: `${activeMeta?.title ?? "Settings"} saved` });
      setDialogOpen(false);
      setActiveSection(null);
      setDraft(null);
    } catch (error) {
      toast({
        title: "Could not save settings",
        description: getApiErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const bvDraftTotal = draft
    ? draft.bv.selfCashbackPercent + draft.bv.levelCashbackPercent + draft.bv.rewardPercent
    : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Platform Settings"
        description="Commission, payout schedules, and BV reward pool configuration."
      />

      <StatsGrid columns={4}>
        <CompactStatCard
          title="Store commission"
          value={`${settings.commission.defaultStoreCommission}%`}
          icon={Percent}
        />
        <CompactStatCard
          title="Delivery per order"
          value={`₹${settings.commission.deliveryCommissionPerOrder}`}
          icon={Truck}
          iconClassName="bg-accent"
        />
        <CompactStatCard
          title="Minimum payout"
          value={`₹${settings.payout.minimumPayout}`}
          icon={IndianRupee}
          iconClassName="bg-gradient-primary"
        />
        <CompactStatCard
          title="BV pool total"
          value={`${(settings.bv.selfCashbackPercent + settings.bv.levelCashbackPercent + settings.bv.rewardPercent).toFixed(0)}%`}
          icon={Award}
        />
      </StatsGrid>

      <Alert className="py-2.5">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Referral level commissions are managed under <strong>Level Settings</strong>. Email and
          payment integrations have their own tabs.
        </AlertDescription>
      </Alert>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {SECTIONS.map((section) => {
          const Icon = section.icon;
          const bvOk = section.key !== "bv" || isBvValid(settings.bv);
          return (
            <div
              key={section.key}
              className="glass-card flex items-center gap-3 rounded-xl border border-border p-3"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <p className="truncate text-sm font-semibold">{section.title}</p>
                  {section.key === "bv" && (
                    <Badge variant={bvOk ? "default" : "destructive"} className="px-1.5 py-0 text-[10px]">
                      {bvOk ? "OK" : "Fix"}
                    </Badge>
                  )}
                </div>
                <p className="truncate text-xs text-muted-foreground">{sectionSummary(settings, section.key)}</p>
              </div>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8 shrink-0"
                title={`Edit ${section.title}`}
                onClick={() => openEdit(section.key)}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            </div>
          );
        })}
      </div>

      <Sheet
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setActiveSection(null);
            setDraft(null);
          }
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-4 text-left">
            <SheetTitle>Edit {activeMeta?.title ?? "Settings"}</SheetTitle>
          </SheetHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {draft && activeSection === "commission" && (
            <div className="space-y-4">
              <FormInput
                label="Default Store Commission (%)"
                type="number"
                value={draft.commission.defaultStoreCommission}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    commission: {
                      ...draft.commission,
                      defaultStoreCommission: Number(e.target.value),
                    } satisfies CommissionSettingsDto,
                  })
                }
              />
              <FormInput
                label="Delivery Commission per Order (₹)"
                type="number"
                value={draft.commission.deliveryCommissionPerOrder}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    commission: {
                      ...draft.commission,
                      deliveryCommissionPerOrder: Number(e.target.value),
                    },
                  })
                }
              />
              <FormInput
                label="Payment Gateway Fee (%)"
                type="number"
                value={draft.commission.paymentGatewayFee}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    commission: {
                      ...draft.commission,
                      paymentGatewayFee: Number(e.target.value),
                    },
                  })
                }
              />
            </div>
          )}

          {draft && activeSection === "payout" && (
            <div className="space-y-4">
              <FormSelect
                label="Store Payout Frequency"
                value={draft.payout.storePayoutFrequency}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    payout: { ...draft.payout, storePayoutFrequency: v } satisfies PayoutSettingsDto,
                  })
                }
                options={[
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ]}
              />
              <FormSelect
                label="Delivery Payout Frequency"
                value={draft.payout.deliveryPayoutFrequency}
                onValueChange={(v) =>
                  setDraft({
                    ...draft,
                    payout: { ...draft.payout, deliveryPayoutFrequency: v },
                  })
                }
                options={[
                  { value: "daily", label: "Daily" },
                  { value: "weekly", label: "Weekly" },
                  { value: "monthly", label: "Monthly" },
                ]}
              />
              <FormInput
                label="Minimum Payout (₹)"
                type="number"
                value={draft.payout.minimumPayout}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    payout: { ...draft.payout, minimumPayout: Number(e.target.value) },
                  })
                }
              />
            </div>
          )}

          {draft && activeSection === "bv" && (
            <div className="space-y-4">
              <FormInput
                label="Activation BV Threshold"
                type="number"
                value={draft.bv.activationBvThreshold}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    bv: { ...draft.bv, activationBvThreshold: Number(e.target.value) },
                  })
                }
              />
              <div className="grid gap-4 sm:grid-cols-3">
                <FormInput
                  label="Self Cashback (%)"
                  type="number"
                  value={draft.bv.selfCashbackPercent}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      bv: { ...draft.bv, selfCashbackPercent: Number(e.target.value) },
                    })
                  }
                />
                <FormInput
                  label="Level Cashback (%)"
                  type="number"
                  value={draft.bv.levelCashbackPercent}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      bv: { ...draft.bv, levelCashbackPercent: Number(e.target.value) },
                    })
                  }
                />
                <FormInput
                  label="Reward (%)"
                  type="number"
                  value={draft.bv.rewardPercent}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      bv: { ...draft.bv, rewardPercent: Number(e.target.value) },
                    })
                  }
                />
              </div>
              <FormInput
                label="Coupons on Activation"
                type="number"
                value={draft.bv.couponsOnIdActivation}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    bv: { ...draft.bv, couponsOnIdActivation: Number(e.target.value) },
                  })
                }
              />
              <FormInput
                label="Commission hold after delivery (hours)"
                type="number"
                value={draft.bv.commissionHoldHours ?? 24}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    bv: { ...draft.bv, commissionHoldHours: Number(e.target.value) },
                  })
                }
              />
              <p className="text-xs text-muted-foreground">
                Return window before BV commission is paid. Default 24 hours (packaged goods policy).
              </p>
              <Alert variant={isBvValid(draft.bv) ? "default" : "destructive"}>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  BV pool total: <strong>{bvDraftTotal.toFixed(2)}%</strong>. Self + Level + Reward
                  must equal 100%, and activation threshold must be positive.
                </AlertDescription>
              </Alert>
            </div>
          )}
          </div>

          <SheetFooter className="mt-auto border-t bg-background px-6 py-4 sm:flex-row sm:justify-end sm:gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={updateSettings.isPending}>
              Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default PlatformSettingsSection;
